import {isEqual} from 'lodash';
import {convertFromDraftJsToHtml} from './DraftJsToHtmlConverter';
import {convertFromHtmlToDraftJs} from './HtmlToDraftJsConverter';

type RawBlock = {key?: string; text?: string} & Record<string, unknown>;
type RawDraft = {blocks: RawBlock[]; entityMap: Record<string, unknown>};

// Lives next to the converters, rather than in the Notes panel, so the panel's blank check and the
// content compare below can never disagree about what "empty" means.
// True for null/empty content and for a draft-js doc that has no text and no entities (e.g. the
// editor was opened via "Create Note" and blurred without typing).
export const isBlankDraftJsContent = (content: string | null | undefined): boolean => {
	if (!content || content.trim() === '') {
		return true;
	}
	try {
		const parsed = JSON.parse(content);
		if (parsed && Array.isArray(parsed.blocks)) {
			const hasText = parsed.blocks.some((block: {text?: string}) => (block.text ?? '').trim() !== '');
			const hasEntities = parsed.entityMap && Object.keys(parsed.entityMap).length > 0;
			return !hasText && !hasEntities;
		}
	} catch {
		// Not draft-js JSON — fall through to a tag-stripped check.
	}
	return (
		content
			.replace(/<[^>]*>/g, '')
			.replace(/&nbsp;/g, ' ')
			.trim() === ''
	);
};

const parseDraftJs = (content: string): RawDraft | null => {
	try {
		const parsed = JSON.parse(content);
		if (parsed && Array.isArray(parsed.blocks)) {
			return {blocks: parsed.blocks, entityMap: parsed.entityMap ?? {}};
		}
	} catch {
		// Not JSON.
	}
	return null;
};

type RawRange = {offset: number; length: number} & Record<string, unknown>;

// The edge-trim whitespace set. ECMAScript `\s` IS that set — it already covers U+00A0, U+1680,
// U+2000-U+200A, U+2028, U+2029, U+202F, U+205F, U+3000 and U+FEFF. The Java half of this trim
// (`ClientRecordMapper.trimNotesEdges`) cannot use `String.trim()` or `String.strip()` — neither
// strips a non-breaking space — and spells the class out instead. If either side changes, change both.
const LEADING_WHITESPACE = /^\s+/;
const TRAILING_WHITESPACE = /\s+$/;

const entityRangesOf = (block: RawBlock): RawRange[] => (block.entityRanges as RawRange[]) ?? [];

// A block is blank when its text trims away AND it carries no entity: a block holding a mention but
// no text is content, and must survive the edge trim.
const isBlankBlock = (block: RawBlock): boolean => (block.text ?? '').trim() === '' && entityRangesOf(block).length === 0;

// Move every range back by the characters removed ahead of it, clamp it to the new end of the text,
// and drop it if the trim consumed it. Without this the trim achieves nothing on a styled note — the
// ranges alone would keep the two sides unequal — and, since the trimmed document is also what gets
// stored, an unshifted range would corrupt the note.
const shiftRanges = (ranges: RawRange[] | undefined, removedAhead: number, newLength: number): RawRange[] =>
	(ranges ?? [])
		.map(range => {
			const offset = Math.max(0, range.offset - removedAhead);
			const end = Math.min(newLength, range.offset + range.length - removedAhead);
			return {...range, offset, length: end - offset};
		})
		.filter(range => range.length > 0);

const trimBlockText = (block: RawBlock, trimStart: boolean, trimEnd: boolean): RawBlock => {
	const text = block.text ?? '';
	const leading = trimStart ? LEADING_WHITESPACE.exec(text)?.[0].length ?? 0 : 0;
	const trailing = trimEnd ? TRAILING_WHITESPACE.exec(text)?.[0].length ?? 0 : 0;
	if (leading === 0 && trailing === 0) {
		return block;
	}
	// Both ends can overlap when the text is nothing but whitespace. That only reaches here for a
	// block carrying an entity — one without is dropped as blank above — and emptying it would strip
	// the entity ranges with it, so leave the block untouched. Stated explicitly rather than left to
	// slice() clamping, because the Java half needs the same guard to avoid an out-of-range substring.
	if (leading + trailing >= text.length) {
		return block;
	}
	const trimmed = text.slice(leading, text.length - trailing);
	// Never empty a block that carries an entity: its ranges would go with the text and the mention
	// would be lost.
	if (trimmed === '' && entityRangesOf(block).length > 0) {
		return block;
	}
	return {
		...block,
		text: trimmed,
		...(block.inlineStyleRanges === undefined
			? {}
			: {inlineStyleRanges: shiftRanges(block.inlineStyleRanges as RawRange[], leading, trimmed.length)}),
		...(block.entityRanges === undefined
			? {}
			: {entityRanges: shiftRanges(block.entityRanges as RawRange[], leading, trimmed.length)}),
	};
};

/**
 * The canonical edge trim on a parsed document. Returns the SAME object when there is no edge to
 * trim, so callers can tell a no-op apart by reference.
 */
const trimDraftJsEdgesOf = <T extends RawDraft>(draft: T): T => {
	const blocks = draft.blocks;
	let first = 0;
	let last = blocks.length - 1;
	while (first <= last && isBlankBlock(blocks[first])) {
		first++;
	}
	while (last >= first && isBlankBlock(blocks[last])) {
		last--;
	}
	// Never trim a blank document: `blocks: []` is not a valid Draft.js document, and clearing a note
	// legitimately stores the one-empty-block doc.
	if (first > last) {
		return draft;
	}
	const kept = blocks.slice(first, last + 1);
	const trimmed = kept.map((block, index) => trimBlockText(block, index === 0, index === kept.length - 1));
	const unchanged = trimmed.length === blocks.length && trimmed.every((block, index) => block === blocks[index]);
	return unchanged ? draft : {...draft, blocks: trimmed};
};

/**
 * Whitespace at the very start or end of a note renders as nothing, so it is not content: a blur
 * that adds only that has not changed the note the user sees, and must not move a timestamp that
 * claims it did. Leading and trailing blank blocks are dropped, then the first and last surviving
 * block's text is trimmed with its ranges shifted and clamped. Interior blank blocks are content.
 *
 * Used by `isSameDraftJsContent` below AND by the Notes panel before it sends, so the compare and
 * the save can never disagree about what an edge is. Mirrored in Java by
 * `ClientRecordMapper.trimNotesEdges`, which is the authoritative half.
 *
 * Content that is not Draft.js JSON, and a document with no edge to trim, come back byte-identical:
 * the panel sends this value, and re-serializing an untouched document would reorder its fields and
 * drop any top-level key the stored note carried.
 */
export const trimDraftJsEdges = (content: string): string => {
	let parsed: unknown;
	try {
		parsed = JSON.parse(content);
	} catch {
		return content;
	}
	if (!parsed || typeof parsed !== 'object' || !Array.isArray((parsed as RawDraft).blocks)) {
		return content;
	}
	const document = parsed as RawDraft & Record<string, unknown>;
	const trimmed = trimDraftJsEdgesOf(document);
	return trimmed === document ? content : JSON.stringify(trimmed);
};

// Block types the design-system editor cannot give back. Plate renders every node through its
// registered component, and the design-system's `getComponents`
// (`RichTextEditorConfiguration.js`) registers `ELEMENT_H1`…`ELEMENT_H3` only — so an h4/h5/h6
// block is serialized as a plain one on export. The converters alone do NOT show this: they
// round-trip `<h4>` straight back to `header-four`. Measured on the Fly preview during the
// IGNITE-625 QA run, which found h1-h3 and blockquote surviving and h4-h6 downgraded.
const BLOCK_TYPES_THE_EDITOR_DOWNGRADES: Record<string, string> = {
	'header-four': 'unstyled',
	'header-five': 'unstyled',
	'header-six': 'unstyled',
};

const applyEditorNormalization = (draft: RawDraft): RawDraft => ({
	blocks: draft.blocks.map(block => {
		const downgraded = BLOCK_TYPES_THE_EDITOR_DOWNGRADES[block.type as string];
		return downgraded === undefined ? block : {...block, type: downgraded};
	}),
	entityMap: draft.entityMap,
});

// Block types the editor never even receives. `convertFromDraftJsToHtml` emits nothing for a
// `code-block`, so the line is gone before Plate sees the note. It has to be removed here rather
// than left for the round trip to swallow, because the two disagree about the gap it leaves: the
// converter emits a blank line and `convertFromHtmlToDraftJs` merges that into the previous block
// as a trailing space ("Line before the code "), while Plate drops the whitespace-only node and
// exports "Line before the code". Stripping the block up front keeps the expected export
// byte-identical to the editor's. Measured off the wire on the Fly preview (IGNITE-625 QA, TC039).
const BLOCK_TYPES_THE_EDITOR_NEVER_RECEIVES = ['code-block'];

const asTheEditorReceivesIt = (content: string): string => {
	const parsed = parseDraftJs(content);
	if (parsed === null) {
		return content;
	}
	const blocks = parsed.blocks.filter(block => !BLOCK_TYPES_THE_EDITOR_NEVER_RECEIVES.includes(block.type as string));
	// Nothing dropped: leave it alone. Nothing left: also leave it alone, because an empty block list
	// would send a JSON string through the HTML converter. Be clear about what that second case then
	// does — a note that is ONLY code-blocks exports as blank, so the compare takes the blank-vs-
	// non-blank branch, returns "changed", and the panel saves the blank note over it. That predates
	// this compare (the same thing happened before any of it existed) and is not fixed here; it is the
	// most destructive face of the `code-block` converter defect the spec records as needing its own
	// ticket, and it needs fixing there, not by widening this equality.
	if (blocks.length === parsed.blocks.length || blocks.length === 0) {
		return content;
	}
	return JSON.stringify({...parsed, blocks});
};

/**
 * What the panel gets back from the editor for a stored note when the user edits nothing: the note
 * through the converter pair the editor runs on either side of itself, plus the editor's own
 * normalization on both ends. A stored note of any shape (converter JSON, old-editor JSON, raw HTML)
 * comes out converter-shaped, which is the shape every editor export has.
 */
const uneditedExportOf = (storedContent: string): RawDraft | null => {
	const roundTripped = parseDraftJs(convertFromHtmlToDraftJs(convertFromDraftJsToHtml(asTheEditorReceivesIt(storedContent))));
	return roundTripped === null ? null : applyEditorNormalization(roundTripped);
};

// Draft.js assigns a fresh random `key` to every block each time a ContentState is rebuilt; it
// carries no content.
const withoutBlockKeys = (draft: RawDraft): RawDraft => ({
	blocks: draft.blocks.map(block => {
		const rest = {...block};
		delete rest.key;
		return rest;
	}),
	entityMap: draft.entityMap,
});

/**
 * True when `exportedContent` is what the editor produces from `storedContent` with no edit made —
 * i.e. there is nothing to save.
 *
 * **The comparison is one-sided, and the argument order is load-bearing.** Only the stored side is
 * put through the editor's pipeline; the exported side is compared as it arrived. Canonicalizing
 * both sides instead would erase whatever the export drops from BOTH of them, and a real edit to
 * one of those fields — an alignment the user just applied, say — would compare equal, the save
 * would be skipped and the edit lost with no error. Here that edit is visible: the expected export
 * has no alignment, the actual one does.
 *
 * The converse asymmetry is the IGNITE-625 bug itself and is what this direction fixes: the export
 * is lossy for several stored shapes (per-block `data` such as `text-align`, mixed-list block
 * order, `header-four`…`header-six`, and `code-block`, which is deleted outright), so comparing the
 * two raw forms reports a change on a blur where the user did nothing, and the panel saves a
 * degraded note. Measured, with evidence, in the IGNITE-625 QA run — see the spec's "Canonicalize
 * the stored side only" decision.
 *
 * Blank compares equal to blank. If the stored side cannot be put through the pipeline, or either
 * side is not Draft.js JSON, falls back to a trimmed string compare.
 */
export const isSameDraftJsContent = (
	storedContent: string | null | undefined,
	exportedContent: string | null | undefined
): boolean => {
	const storedBlank = isBlankDraftJsContent(storedContent);
	const exportedBlank = isBlankDraftJsContent(exportedContent);
	if (storedBlank || exportedBlank) {
		return storedBlank && exportedBlank;
	}
	const stored = storedContent as string;
	const exported = exportedContent as string;
	let expected: RawDraft | null;
	try {
		expected = uneditedExportOf(stored);
	} catch {
		return stored.trim() === exported.trim();
	}
	const actual = parseDraftJs(exported);
	// The stored note is not Draft.js after conversion, the export is not Draft.js at all, or the
	// converters swallowed a non-blank note — do not trust a compare of two empty documents.
	if (expected === null || actual === null || isBlankDraftJsContent(JSON.stringify(expected))) {
		return stored.trim() === exported.trim();
	}
	// Edge whitespace is the one normalization here applied SYMMETRICALLY, to both sides. Every
	// other normalization above runs on the stored side only (see the note on argument order),
	// because canonicalizing the export would erase a real edit from it. The distinction is what the
	// erased edit is: those protect edits invisible to the EXPORTER but visible on screen (an
	// alignment, a heading level), while this one erases an edit that is invisible ON SCREEN.
	const canonical = (document: RawDraft): RawDraft => trimDraftJsEdgesOf(withoutBlockKeys(document));
	return isEqual(canonical(expected), canonical(actual));
};
